import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoggingService } from '../utils/logging.service';
import { UriUtil } from '../utils/uri-util';
import { Language } from './language';

// Default, so lange der Portalservice keine Daten aus dem Backend abgerufen hat
const DEFAULT_LANGUAGES = new Map<string, Language>();
DEFAULT_LANGUAGES.set('de', new Language('de', 'Deutsch'));
DEFAULT_LANGUAGES.set('en', new Language('en', 'Englisch'));

/**
 * Service zum Setzen der Anzeigesprache des Portals.
 */
@Injectable()
export class LanguageService implements OnDestroy {
    private static LOCAL_STORAGE_ITEM = 'portal_language';

    public currentLanguage: string = 'de';
    public languageChanged: Subject<string> = new ReplaySubject<string>(1);

    // Ob eine Sprache im Storage gespeichert war und der Service mit dieser Sprache initialisiert wurde.
    public fromStorage: boolean;

    public availableLanguages: Map<string, Language> = DEFAULT_LANGUAGES;
    public availableLanguagesSubject = new BehaviorSubject<Map<string, Language>>(DEFAULT_LANGUAGES);

    private ngUnsubscribe$ = new Subject<void>();

    /**
     * Gibt an ob die interne Subscription auf availableLanguagesSubject die erste ist bzw. ob es noch
     * der initialwert ist.
     */
    private isFirstValue = true;

    /**
     * Initialisiert neben einer statischen Initialisierung der Sprache des Nutzers vor allem, dass zur
     * Laufzeit die Sprachenauswahl dynamisch geändert werden kann sowie auch die ausgewählte Sprache selbst.
     * Dafür wird auf dem Subject subscribed. Stellt immer sicher, dass eine valide/existierende Sprache ausgewählt wird.
     *
     * @param log
     */
    constructor(private log: LoggingService) {
        // Machen zuerst statisches Init der Sprache, bevor irgendetwas vom Backend abgerufen wurde

        // Sprachparameter explizit in der URI mitgegeben?
        const langFromUri = UriUtil.getFragment()['lang'];
        // Der Check auf die verfügbaren Sprachen funktioniert hier (noch) nicht, da die über die Portal-Konfig geladenen Sprachen
        // erst später initialisiert werden.
        if (langFromUri) {
            this.setLanguage(LanguageService.sanitize(langFromUri));
        } else {
            // sonst: erst im local storage schauen; danach im navigator (browser)
            const langFromLocalStorage = localStorage.getItem(LanguageService.LOCAL_STORAGE_ITEM);
            const langFromNavigator = navigator.language.substr(0, 2);
            this.fromStorage = this.availableLanguages.has(langFromLocalStorage);
            // Falls Sprache im Local Storage gesetzt ist, dann wird diese verwendet. Sonst die Browsersprache.
            this.setLanguage(this.fromStorage ? langFromLocalStorage : LanguageService.sanitize(langFromNavigator));
        }

        /*
         * Horchen zur Laufzeit die ganze Zeit auf Veränderungen. Wenn beispielsweise das Portal ein neues Set
         * an verfügbaren Sprachen zeigt, dann wird dieser Block aktiv. Es wird dann immer geschaut, ob die
         * currentLanguage noch funkionieren kann. Wenn nicht wird ein Default gesetzt.
         */
        this.availableLanguagesSubject.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(languages => {
            this.availableLanguages = languages;

            if (this.isFirstValue) {
                this.isFirstValue = false;
                this.log.debug('LanguageService#constructor#subscription: Verfügbare Sprachen wurden initial (hart im Code) gesetzt:');
            } else {
                this.log.debug('LanguageService#constructor#subscription: Verfügbare Sprachen wurden von externer Stelle gesetzt:');
            }
            // this.log.debug(languages.toJSON()); // geht nicht obwohl es eine Map ist
            this.log.debug(Array.from(languages.values()));


            // wollen überprüfen, ob die current language noch aktuell ist/funktioniert
            if (!this.availableLanguages.has(this.currentLanguage)) {
                // this.setLanguage bemerkt selbst, dass currentLanguage nicht mehr verfügbar ist
                this.log.debug(
                    `LanguageService#constructor#subscription: Gesetzte Sprache "${this.currentLanguage}" nun nicht mehr verfügbar!`
                );
                this.setLanguage(this.currentLanguage);
            }
        });
    }

    /**
     * Setzt die Sprache des aktuellen Nutzers MIT {@link #registerForLanguageChanged}-Event.
     * Siehe {@link #setLanguage}. Zum Beispiel sinnvoll, wenn der Nutzer in der Sprachauswahl
     * seine Sprache ändert. So werden alle Komponenten die auf {@link languageChanged} horchen
     * darüber informiert.
     *
     * @param lang
     */
    public change(lang: string) {
        this.setLanguage(lang);
        this.languageChanged.next(this.currentLanguage);
        this.log.info(`LanguageService#change: LanguageChanged-Event ausgelöst (${this.currentLanguage})`);
    }

    /**
     * Setzt die Sprache des aktuellen Nutzers OHNE {@link #languageChanged}-Event.
     * Um das Event auszulösen siehe {@link #change}.
     *
     * Führt einen abgleich
     * mit den vorhandenen Sprachen aus und setzt ggf. die ausgewählte Sprache des Nutzers zurück, wenn
     * sie nicht (mehr) verfügbar ist.
     *
     * @param lang
     */
    public setLanguage(lang: string): void {
        if (!this.availableLanguages || !this.availableLanguages.size) {
            this.log.error(
                `LanguageService#setLanguage: availableLanguages ist null oder leer! Kann Sprache "${lang}" NICHT setzen!`
            );
            return;
        }

        // Müssen Fehlerbehandlung machen
        if (!this.availableLanguages.has(lang)) {
            // hart einprogrammiert wie im Backend; wollen de benutzen, wenn ein Nutzer eine unbekannte Sprache
            // hat; z.B. "ru" (Russisch)
            if (this.availableLanguages.has('de')) {
                this.currentLanguage = 'de';
            } else {
                // wenn de nicht vorhanden ist, nutzen wir die erste Sprache, die verfügbar ist
                const firstLang = this.availableLanguages.keys()[0];
                this.currentLanguage = this.availableLanguages.get(firstLang).language;
            }

            this.log.debug(
                `LanguageService#setLanguage: Sprache des Nutzers wurde zwangsweise ` +
                `auf die Standardsprache "${this.currentLanguage}" gesetzt (da "${lang}" nicht vorhanden ist)`
            );
        } else {
            this.currentLanguage = LanguageService.sanitize(lang);
            this.log.debug(`LanguageService#setLanguage: Sprache des Nutzers ist jetzt "${this.currentLanguage}"`);
        }

        // persistieren
        localStorage.setItem(LanguageService.LOCAL_STORAGE_ITEM, this.currentLanguage);
    }

    /**
     * Diese Methode wird von Komponenten aufgerufen, die bei Änderungen der Sprache ihre angezeigten Texte ändern möchten.
     *
     * @param component die Komponente
     * @param propertyName der Name der Property, die alle lokalisierten Texte enthalten soll
     * @param i18nMessages Objekt mit den Texten in den unterschiedlichen Sprachvariante. Unter der
     *      Property "de" sind alle deutschen Texte, unter "en" sind alle englischen Texte.
     * @return die Subscription. Auf ihr muss von der Komponente in ihrem ngOnDestroy
     *      die Methode unsubscribe aufgerufen werden, sonst drohen Memory Leaks.
     */
    public registerForLanguageChanged(component: any, propertyName: string, i18nMessages: any): Subscription {
        component[propertyName] = i18nMessages[this.currentLanguage] || i18nMessages['en'];
        return this.languageChanged.subscribe(lang => {
            component[propertyName] = i18nMessages[lang] || i18nMessages['en'];
        });
    }

    /**
     * Gibt die aktuell aktive Sprache zurück.
     */
    public getLanguage(): string {
        return this.currentLanguage;
    }

    /**
     * Gibt die möglichen Sprachen als Observable zurück. Beim subscriben wird der letzte Wert
     * erneut durchgegeben, d.h. gilt das nicht nur für neue Events.
     */
    public getAvailableLanguages(): Observable<Map<string, Language>> {
        return this.availableLanguagesSubject.asObservable();
    }

    /**
     * Setzt die verfügbaren Sprachen. Wird im Portal genutzt um die vom Backend
     * abgerufenen verfügbaren Sprachen anzuzeigen. Intern wird eine subscription in
     * einem potentiellen Seiteneffekt die Sprache des Nutzers zurücksetzen,
     * wenn sie nicht in den languages vorhanden ist.
     *
     * @param languages
     */
    public setAvailableLanguages(languages: Map<string, Language>) {
        this.availableLanguagesSubject.next(languages);
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * Überprüft, dass eine Sprache 2 Zeichen lang ist. Ansonsten wird 'de' genommen. (ISO-Code)
     *
     * @param lang
     */
    private static sanitize(lang: string) {
        const l = lang && lang.substr(0, 2);
        return l || 'de';
    }
}
